import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PageService, SePageComponent } from '@se/platform';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BusinessUnit, PatientDetails } from '../../models/dto/patient-details';
import { PatientDetailsForm } from '../../models/PatientDetailsForm';
import { distinctUntilChanged } from 'rxjs/operators';
import { OrderBusinessUnitService } from '../../../../order-business-unit.service';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatOption, MatSelect } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'patient-details-form',
    templateUrl: './patient-details-form.component.html',
    styleUrls: ['./patient-details-form.component.scss'],
    imports: [
        MatFormField,
        MatLabel,
        MatFormField,
        NgClass,
        MatDatepickerToggle,
        MatDatepicker,
        MatDatepickerInput,
        MatSelect,
        NgIf,
        MatOption,
        NgForOf,
        TranslatePipe,
        ReactiveFormsModule,
        MatInput
    ],
    standalone: true,
})
export class PatientDetailsFormComponent extends SePageComponent implements OnInit {
    @Input() patientInfo: PatientDetails;
    @Input() isVNA: boolean;

    @Output() patientChanges: EventEmitter<PatientDetails> = new EventEmitter<PatientDetails>();

    options: Array<BusinessUnit>;
    patientDataForm: FormGroup<PatientDetailsForm>;

    constructor(
        pageService: PageService,
        private orderBusiness: OrderBusinessUnitService,
        private fb: FormBuilder,
    ) {
        super(pageService);
        this.options = [];
    }

    ngOnInit() {
        if (this.isVNA) {
            this.getBusinessOrderList();
        }
        this.patientDataForm = this.buildForm();
        if (this.isVNA) {
            this.patientDataForm.addControl('businessUnit', this.businessUnitField());
        }
        if (this.patientDataForm) {
            this.patientChanges.emit(this.getPatientDetails());
        }
        this.patientDataForm.valueChanges
            .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((value) => {
                this.patientChanges.emit(this.getPatientDetails());
            });
    }

    isDisabled(controlName: string): boolean {
        return this.patientDataForm.controls[controlName].disabled;
    }

    private getBusinessOrderList() {
        this.orderBusiness.getBusinessUnitsFromAll().subscribe((items) => {
            this.options = [...items];
        });
    }

    private businessUnitField(): FormControl<BusinessUnit> {
        let control: FormControl<BusinessUnit> = this.formDataBuilderRequired(this.patientInfo, 'BusinessUnit');
        return control;
    }

    private getPatientDetails(): PatientDetails {
        let patient: PatientDetails = {
            Issuer: this.patientDataForm.controls['issuer'].value,
            PatientId: this.patientDataForm.controls['patientId'].value,
            Name: this.patientDataForm.controls['name'].value,
            Surname: this.patientDataForm.controls['surname'].value,
            Sex: this.patientDataForm.controls['sex'].value,
            BirthDate: this.patientDataForm.controls['birthDate'].value,
            AccessionNumber: this.patientDataForm.controls['accessionNumber'].value,
            Status: this.patientDataForm.status,
        };

        if (this.isVNA) {
            patient.BusinessUnit = this.patientDataForm.controls['businessUnit'].value;
        }

        return patient;
    }

    private buildForm(): FormGroup<PatientDetailsForm> {
        return this.fb.group({
            issuer: this.formDataBuilderRequired(this.patientInfo, 'Issuer'),
            patientId: this.formDataBuilderRequired(this.patientInfo, 'PatientId'),
            name: this.formDataBuilderRequired(this.patientInfo, 'Name'),
            surname: this.formDataBuilderRequired(this.patientInfo, 'Surname'),
            sex: this.formDataBuilderRequired(this.patientInfo, 'Sex'),
            birthDate: this.formDataBuilderRequired(this.patientInfo, 'BirthDate'),
            accessionNumber: this.formDataBuilderOptional(this.patientInfo, 'AccessionNumber'),
        });
    }

    private formDataBuilderRequired(source: PatientDetails, fieldName: string): FormControl {
        return this.fb.control(
            {
                value: source[fieldName],
                disabled: this.manageFieldsAbilitation(source, fieldName),
            },
            [Validators.required],
        );
    }

    private formDataBuilderOptional(source: PatientDetails, fieldName: string): FormControl {
        return this.fb.control({
            value: source[fieldName],
            disabled: this.manageFieldsAbilitation(source, fieldName),
        });
    }

    private manageFieldsAbilitation(source: PatientDetails, fieldName: string): boolean {
        return !!source[fieldName];
    }
}
